// Design Tokens for Wild Network
// This file contains all branding colors, typography, and component styles
// to ensure consistency across the application.

import { wildNetworkGreenUi, wildNetworkPalette } from '@/theme/wildNetworkPalette';

const { earth, neutral } = wildNetworkPalette;

export const colors = {
  // Primary — Wild Network earth forest (single source: theme/wildNetworkPalette.ts)
  primary: earth.forest,
  primaryHover: earth.forestDark,
  primaryLight: earth.forestMuted,
  primaryDark: neutral.inkDeep,

  secondary: earth.forestMuted,
  secondaryLight: wildNetworkGreenUi.wash,

  /** App canvas (parchment light); cards use custom1 / neutral.white */
  background: neutral.paper,
  backgroundSecondary: neutral.pageSubtle,
  backgroundTertiary: wildNetworkGreenUi.surface3,
  backgroundQuaternary: wildNetworkGreenUi.surface4,

  border: wildNetworkGreenUi.border,
  borderHover: wildNetworkGreenUi.borderHover,
  borderLight: wildNetworkGreenUi.borderLight,
  borderDark: wildNetworkGreenUi.borderDark,

  /** Body text (spec); headings use inkDeep / site-color-text-heading in CSS */
  textPrimary: neutral.ink,
  textSecondary: earth.forest,
  textTertiary: earth.forestMuted,
  textLight: wildNetworkGreenUi.wash,

  focus: earth.forest,
  success: earth.forest,
  error: '#834844',
  warning: '#ffc107',

  danger: '#834844',
  dangerLight: '#D5B1AF',

  overlay: 'rgba(0, 0, 0, 0.5)',
  shadow: 'rgba(0, 0, 0, 0.12)',

  // Radix custom scale (values from wildNetworkGreenUi + earth + neutral)
  custom1: wildNetworkGreenUi.surface1,
  custom2: wildNetworkGreenUi.surface2,
  custom3: wildNetworkGreenUi.surface3,
  custom4: wildNetworkGreenUi.surface4,
  custom5: wildNetworkGreenUi.border,
  custom6: wildNetworkGreenUi.borderHover,
  custom7: wildNetworkGreenUi.borderLight,
  custom8: wildNetworkGreenUi.borderDark,
  custom9: earth.forest,
  custom10: earth.forestDark,
  custom11: earth.forestMuted,
  custom12: neutral.inkDeep
};

export const typography = {
  /** Primary UI / body text — Cabin (matches mobile) */
  fontFamilyUi: '"Cabin", sans-serif',
  /** Labels, data readouts, buttons — DM Mono */
  fontFamily: '"DM Mono", monospace',
  /** Species & long-form prose */
  fontFamilyEditorial: '"Lora", serif',
  letterSpacing: '0',
  /** Typography guide — UI labels / micro caps */
  letterSpacingLabel: '0.18em',
  letterSpacingData: '0.06em',
  letterSpacingWordmark: '-0.02em',
  lineHeightFieldNote: 1.55,
  
  // Font Sizes
  xs: '12px',
  sm: '14px',
  base: '16px',
  lg: '18px',
  xl: '20px',
  '2xl': '24px',
  '3xl': '32px',
  '4xl': '48px',
  '5xl': '64px',
  
  // Font Weights
  weightNormal: 400,
  weightMedium: 500,
  weightSemibold: 600,
  weightBold: 700,
  
  // Line Heights
  lineHeightTight: 1.1,
  lineHeightNormal: 1.5,
  lineHeightRelaxed: 1.6,
};

export const spacing = {
  xs: '4px',
  sm: '8px',
  md: '12px',
  lg: '16px',
  xl: '20px',
  '2xl': '24px',
  '3xl': '32px',
  '4xl': '48px',
  '5xl': '80px',
};

export const borderRadius = {
  sm: '4px',
  md: '8px',
  lg: '12px',
  xl: '16px',
};

const shadows = {
  sm: '0 1px 3px rgba(0, 0, 0, 0.1)',
  md: '0 4px 20px rgba(0, 0, 0, 0.12)',
  lg: '0 10px 25px rgba(0, 0, 0, 0.15)',
};

export const transitions = {
  fast: '0.15s ease',
  normal: '0.2s ease',
  slow: '0.3s ease',
};

// Component-specific styles
const buttonStyles = {
  primary: {
    background: colors.primary,
    color: colors.background,
    border: 'none',
    borderRadius: borderRadius.md,
    padding: `${spacing.lg} ${spacing['2xl']}`,
    fontSize: typography.base,
    fontWeight: typography.weightMedium,
    cursor: 'pointer',
    transition: `all ${transitions.fast}`,
    fontFamily: typography.fontFamily,
    letterSpacing: typography.letterSpacing,
    '&:hover': {
      background: colors.primaryHover,
      transform: 'translateY(-1px)',
    },
    '&:active': {
      transform: 'translateY(0)',
    },
  },
  
  secondary: {
    background: 'none',
    color: colors.textSecondary,
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.md,
    padding: `${spacing.lg} ${spacing['2xl']}`,
    fontSize: typography.base,
    fontWeight: typography.weightMedium,
    cursor: 'pointer',
    transition: `all ${transitions.fast}`,
    fontFamily: typography.fontFamily,
    letterSpacing: typography.letterSpacing,
    '&:hover': {
      background: colors.backgroundTertiary,
      borderColor: colors.borderHover,
      transform: 'translateY(-1px)',
    },
    '&:active': {
      transform: 'translateY(0)',
    },
  },
  
  close: {
    background: 'none',
    border: 'none',
    fontSize: typography['2xl'],
    cursor: 'pointer',
    color: colors.textSecondary,
    padding: spacing.sm,
    width: '40px',
    height: '40px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: borderRadius.sm,
    transition: `background-color ${transitions.fast}`,
    flexShrink: 0,
    '&:hover': {
      backgroundColor: colors.backgroundTertiary,
    },
  },
};

const inputStyles = {
  base: {
    width: '100%',
    padding: `${spacing.md} ${spacing.lg}`,
    fontSize: typography.base,
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.md,
    fontFamily: typography.fontFamily,
    transition: `border-color ${transitions.fast}`,
    boxSizing: 'border-box',
    '&:focus': {
      borderColor: colors.focus,
      outline: 'none',
    },
  },
};

const cardStyles = {
  base: {
    background: neutral.white,
    borderRadius: borderRadius.md,
    boxShadow: shadows.md,
    border: `1px solid ${colors.border}`,
    fontFamily: typography.fontFamily,
  },
  
  mobile: {
    borderRadius: 0,
    border: 'none',
  },
};

/** Site pages (non-map): unified width and spacing. Match header. */
export const siteLayout = {
  contentMaxWidth: 1024,
  contentPaddingX: 24,
  mainPaddingTop: 40,
  mainPaddingBottom: 80,
  mainPaddingTopMobile: 20,
  mainPaddingBottomMobile: 60,
  /** Legal/article content - same as content width on desktop */
  contentReadableMaxWidth: 1024,
};

const layoutStyles = {
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: `0 ${spacing['2xl']}`,
  },
  site: siteLayout,
  sidebar: {
    width: '460px',
    height: '100vh',
    maxHeight: '100vh',
  },
  
  mobile: {
    width: '100vw',
    height: '100vh',
    maxHeight: '100vh',
  },
};

// Responsive breakpoints
export const breakpoints = {
  mobile: '768px',
  tablet: '1024px',
  desktop: '1200px',
};

// Z-index scale
export const zIndex = {
  base: 1,
  dropdown: 100,
  sticky: 200,
  overlay: 1000,
  modal: 2000,
  tooltip: 3000,
};

// Export everything as a single object for easy importing
export const designTokens = {
  colors,
  typography,
  spacing,
  borderRadius,
  shadows,
  transitions,
  buttonStyles,
  inputStyles,
  cardStyles,
  layoutStyles,
  siteLayout,
  breakpoints,
  zIndex,
};

export default designTokens; 